// src/stores/sizingStore.ts
// Global store for sizing state - enables cross-page state sharing
import type { NewSizingSnapshot, NewSizingRequest } from '@/sizing/newSizingEngine';

type SizingStoreState = {
  snapshot: NewSizingSnapshot | null;
  request: NewSizingRequest | null;
  status: 'idle' | 'running' | 'completed' | 'error';
  error: Error | null;
};

const STORAGE_KEY = 'sizing_snapshot_cache';

function loadFromStorage(): Partial<SizingStoreState> {
  try {
    const cached = typeof window !== 'undefined' ? window.sessionStorage?.getItem(STORAGE_KEY) : null;
    if (!cached) return {};
    const parsed = JSON.parse(cached);
    return {
      snapshot: parsed.snapshot ?? null,
      request: parsed.request ?? null,
      status: parsed.snapshot ? 'completed' : 'idle',
    };
  } catch {
    return {};
  }
}

function saveToStorage(snapshot: NewSizingSnapshot, request: NewSizingRequest) {
  try {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ snapshot, request }));
    }
  } catch {
    // Ignore storage errors
  }
}

function clearStorage() {
  try {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

const restored = loadFromStorage();

let state: SizingStoreState = {
  snapshot: restored.snapshot ?? null,
  request: restored.request ?? null,
  status: restored.status ?? 'idle',
  error: null,
};

const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((cb) => cb());
}

export const sizingStore = {
  subscribe(cb: () => void) {
    subscribers.add(cb);
    return () => {
      subscribers.delete(cb);
    };
  },

  getSnapshot() {
    return state;
  },

  setSnapshot(snapshot: NewSizingSnapshot, request: NewSizingRequest) {
    state = { ...state, snapshot, request, status: 'completed', error: null };
    saveToStorage(snapshot, request);
    notify();
  },

  setStatus(status: SizingStoreState['status']) {
    state = { ...state, status };
    notify();
  },

  setError(error: Error) {
    state = { ...state, status: 'error', error };
    notify();
  },

  reset() {
    state = { snapshot: null, request: null, status: 'idle', error: null };
    clearStorage();
    notify();
  },
};

export type { SizingStoreState };
